import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, from, map, of, switchMap } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { environment } from "../../../environments/environment";
import { SKIP_ERROR_ALERT } from "../interceptors/skip-error-alert.context";
import { MapPoint, isShortGoogleMapsUrl, mapsPlaceQuery, parseGoogleMapsUrl } from "../utils/google-maps-url.util";
import { GoogleMapsLoaderService } from "./google-maps-loader.service";

/** Turns any Google Maps link a merchant has into a position, so nobody has to place a pin they already shared. */
@Injectable({ providedIn: "root" })
export class MapsLinkService {
  constructor(
    private readonly http: HttpClient,
    private readonly mapsLoader: GoogleMapsLoaderService,
  ) {}

  /**
   * The position of a link: read from the link itself; a short link is first expanded by the ERP (browsers cannot
   * follow it); a link naming a place without coordinates is looked up by that name. Null when nothing is found.
   */
  pointOf(link: string | null | undefined): Observable<MapPoint | null> {
    return this.expanded(link).pipe(
      switchMap((full) => {
        const point = parseGoogleMapsUrl(full);
        return point ? of(point) : this.pointOfPlace(full);
      }),
    );
  }

  /**
   * Only the coordinates the link really holds, short links included. A name lookup can land on another shop or
   * city, so what is saved without anyone looking comes from here, never from {@link pointOf}.
   */
  coordinatesOf(link: string | null | undefined): Observable<MapPoint | null> {
    return this.expanded(link).pipe(map((full) => parseGoogleMapsUrl(full)));
  }

  /** The link itself, or for a short link the full one behind it (the ERP only follows https links). */
  private expanded(link: string | null | undefined): Observable<string | null | undefined> {
    if (!isShortGoogleMapsUrl(link)) {
      return of(link);
    }
    return this.http
      .get<{ body?: { url?: string } }>(`${environment.erpApiUrl}/v1/api/maps/resolve-link`, {
        params: { url: "https://" + (link as string).trim().replace(/^https?:\/\//i, "") },
        headers: { "x-api-canal": "web", "x-api-requestId": uuidv4() },
        context: new HttpContext().set(SKIP_ERROR_ALERT, true),
      })
      .pipe(
        map((response) => response?.body?.url ?? null),
        catchError(() => of(null)),
      );
  }

  private pointOfPlace(link: string | null | undefined): Observable<MapPoint | null> {
    const address = mapsPlaceQuery(link);
    if (!address) {
      return of(null);
    }
    return from(this.mapsLoader.load()).pipe(
      switchMap(() => new (window as any).google.maps.Geocoder().geocode({ address, region: "ma" }) as Promise<{ results?: any[] }>),
      map(({ results }) => {
        const location = results?.[0]?.geometry?.location;
        return location ? { lat: location.lat() as number, lng: location.lng() as number } : null;
      }),
      catchError(() => of(null)),
    );
  }
}
